import type { FlowProcessState } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiErrorCode } from "@/lib/api/_client";
import * as flowInstancesApi from "@/lib/api/flow-instances";
import * as publicInstanceSnapshotsApi from "@/lib/api/public-flow/instance-snapshots";
import { usePublicSession } from "@/lib/context/public-session-context";
import { flowCompletionQueryKey } from "@/lib/hooks/use-flow-instances/use-flow-completion";

// --- Query Keys ---

const keys = {
  // Instance-wide invalidation prefix — covers all ["flowInstances", id, …] sub-keys.
  // (No query is registered at this exact key since the monolithic read was retired.)
  instance: (id: string | null) => ["flowInstances", id],
  metadata: (id: string | null) => ["flowInstances", id, "metadata"] as const,
  // QL-675 — snapshot rebuild status, polled while a rebuild runs (never cached).
  snapshotStatus: (id: string | null) => ["flowInstances", id, "snapshot-status"] as const,
  flowDefinition: (id: string | null) => ["flowInstances", id, "flow-definition"] as const,
  // Process state (steps, approvals, activity log). Exported so mutations that
  // append a flow_instance_event from outside the instance pages can refresh it.
  process: (id: string | null) => ["flowInstances", id, "process"] as const,
  // Re-export goal keys for backward compatibility (actual hooks are in use-goals.ts)
  goals: (instanceId: string | null) => ["flowInstances", instanceId, "goals"],
  goal: (goalId: string | null) => ["flowInstances", "goals", goalId],
  advisorNotes: (instanceId: string | null, pageId: string | null, goalId?: string | null) => [
    "flowInstances",
    instanceId,
    "advisorNotes",
    pageId,
    goalId,
  ],
  productCosts: (instanceId: string | null) => ["flowInstances", instanceId, "productCosts"],
  // The public twin of productCosts. Outside the public snapshot subtree, so
  // whatever refreshes the public snapshots after a rebuild names it itself.
  publicProductCosts: (instanceId: string | null) =>
    ["publicFlow", "instance", instanceId, "productCosts"] as const,
};

// --- Query Hooks ---

export function useFlowInstanceMetadata(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.metadata(id),
    queryFn: () => flowInstancesApi.getMetadata(id!),
    enabled: !!id,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: keys.metadata(id) });
  return { metadata: data ?? null, error, isLoading, mutate };
}

export function useFlowDefinition(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.flowDefinition(id),
    queryFn: () => flowInstancesApi.getFlowDefinition(id!),
    enabled: !!id,
    staleTime: Number.POSITIVE_INFINITY, // Flow structure never changes during a session
  });
  return { flowDefinition: data ?? null, error, isLoading };
}

export function useAdvisorNote(
  instanceId: string | null,
  pageId: string | null,
  goalId?: string | null,
) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.advisorNotes(instanceId, pageId, goalId),
    queryFn: () => flowInstancesApi.getAdvisorNote(instanceId!, pageId!, goalId ?? undefined),
    enabled: !!instanceId && !!pageId,
  });
  const queryClient = useQueryClient();
  const mutate = () =>
    queryClient.invalidateQueries({
      queryKey: keys.advisorNotes(instanceId, pageId, goalId),
    });
  return { notes: data?.notes ?? null, error, isLoading, mutate };
}

// Public-capable (audit §7). On public, reads `/public/flow/snapshots/product-costs`
// (same service/shape as advisor); advisor query key unchanged.
/** Advisor vs public endpoint comes from `usePublicSession()`. */
export function useProductCosts(instanceId: string | null, lang?: string) {
  const session = usePublicSession();
  const { data, error, isLoading } = useQuery({
    queryKey: session.isPublic
      ? keys.publicProductCosts(instanceId)
      : keys.productCosts(instanceId),
    queryFn: () =>
      session.isPublic
        ? publicInstanceSnapshotsApi.getPublicAllProductCosts(session.sessionToken, lang)
        : flowInstancesApi.getProductCosts(instanceId!),
    enabled: !!instanceId,
  });
  return { productCosts: data ?? null, error, isLoading };
}

export function useProcessState(instanceId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.process(instanceId),
    queryFn: () => flowInstancesApi.getProcessState(instanceId!),
    enabled: !!instanceId,
    // Several actors move the process (advisor, compliance, external systems)
    // — keep open pages from going stale and offering impossible actions.
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: keys.process(instanceId) });
  return { processState: data ?? null, error, isLoading, mutate };
}

export function useSignatures(instanceId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["flowInstances", instanceId, "signatures"],
    queryFn: () => flowInstancesApi.getSignatures(instanceId!),
    enabled: !!instanceId,
  });
  return { signatures: (data ?? []) as any[], error, isLoading };
}

// --- Mutation Hooks ---

export function useSaveAdvisorNote(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { pageId: string; goalId?: string; notes: string }) =>
      flowInstancesApi.saveAdvisorNote(instanceId, data),
    onSuccess: (_result, { pageId, goalId }) => {
      queryClient.invalidateQueries({
        queryKey: keys.advisorNotes(instanceId, pageId, goalId),
      });
      // QL-43 — a page can require its note, so the note is a completion input.
      queryClient.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) });
    },
    // QL-712 — the page's notes were switched off after this wizard loaded: refetch the flow so
    // the editor goes away instead of autosaving into a 400.
    onError: (error) => {
      if (isNotesNotEnabled(error)) {
        queryClient.invalidateQueries({ queryKey: keys.flowDefinition(instanceId) });
      }
    },
  });
}

/** QL-712 — the 400 the API answers a note on a page that does not show the advisor-note field. */
export function isNotesNotEnabled(error: unknown): boolean {
  return apiErrorCode(error) === "advisor_notes_not_enabled";
}

/**
 * QL-399 — dismiss one feedback message for everyone on the flow.
 *
 * Optimistic, because the counter and the card stack are the thing the advisor
 * is looking at: the card goes immediately and is put back on failure, leaving
 * the view and the count exactly as they were so the action can be retried.
 */
export function useDismissProcessMessage(
  instanceId: string | null,
  /** Told when a dismiss fails, so the restored card is explained (QL-399 review). */
  onFailure?: (message: string) => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      flowInstancesApi.dismissProcessMessage(instanceId!, messageId),
    onMutate: async (messageId: string) => {
      const queryKey = keys.process(instanceId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<FlowProcessState>(queryKey);
      if (previous) {
        queryClient.setQueryData<FlowProcessState>(queryKey, {
          ...previous,
          messages: previous.messages.filter((m) => m.id !== messageId),
        });
      }
      return { previous };
    },
    onError: (err, _messageId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(keys.process(instanceId), context.previous);
      }
      // The card reappearing with no explanation reads as a UI glitch.
      onFailure?.(err instanceof Error ? err.message : "The message could not be closed");
    },
    // Dismissal is shared, so settle against the server either way: another
    // user may have closed something else in the meantime.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.process(instanceId) });
    },
  });
}

// --- Exported Query Keys (for targeted invalidation in widget mutations) ---

export { keys as flowInstanceQueryKeys };
